import { useCallback, useState } from 'react'

import { F0Button } from '@factorialco/f0-react'

import i18n from 'modules/core/lib/i18n'

import { Modals } from 'modules/integrations/components/SyncButton/Modals'
import { SyncReviewModal } from 'modules/integrations/components/SyncButton/Modals/SyncReviewModal'
import { SyncFiltersModal } from 'modules/integrations/components/SyncFiltersModal'
import { usePrivateIntegrationsSyncContext } from 'modules/integrations/hooks/SyncContext'
import { useReadSyncIssuesLazyQuery } from 'modules/integrations/hooks/SyncContext/useReadSyncIssuesLazyQuery'
import type { SyncBarStatus } from 'modules/integrations/hooks/useSyncBarData'
import type { SyncFilterDefinition } from 'modules/integrations/lib/syncFilters'

type ModalStep = 'closed' | 'filters' | 'issues' | 'review_issues'

const getSyncActionLabel = (
  status: SyncBarStatus,
  integrationName: string,
  isExportMode: boolean
): string => {
  if (isExportMode) {
    switch (status) {
      case 'pending':
        return i18n.t('integrations.sync_bar.export_action.export_to', {
          integration: integrationName,
        })
      case 'syncing':
        return i18n.t('integrations.sync_bar.export_action.exporting')
      case 'failed':
        return i18n.t('integrations.sync_bar.export_action.review_and_export')
      case 'synced':
        return i18n.t('integrations.sync_bar.export_action.export_to', {
          integration: integrationName,
        })
    }
  }
  switch (status) {
    case 'pending':
      return i18n.t('integrations.sync_bar.action.sync_to', { integration: integrationName })
    case 'syncing':
      return i18n.t('integrations.sync_bar.action.syncing')
    case 'failed':
      return i18n.t('integrations.sync_bar.action.review_and_sync')
    case 'synced':
      return i18n.t('integrations.sync_bar.action.sync_to', { integration: integrationName })
  }
}

interface SyncBarActionProps {
  syncStatus: SyncBarStatus
  integrationName: string
  isExportMode: boolean
  syncFilters?: SyncFilterDefinition[]
  skipConfirmationIfNoIssues?: boolean
  disabled?: boolean
  tooltip?: string
}

export const SyncBarAction = ({
  syncStatus,
  integrationName,
  isExportMode,
  syncFilters = [],
  skipConfirmationIfNoIssues = false,
  disabled = false,
  tooltip,
}: SyncBarActionProps) => {
  const { isSyncing } = usePrivateIntegrationsSyncContext()
  const [modalStep, setModalStep] = useState<ModalStep>('closed')

  const hasIssues = syncStatus === 'failed'
  const { syncResultIssues, loading: issuesLoading } = useReadSyncIssuesLazyQuery({
    skip: isSyncing || !hasIssues || modalStep !== 'review_issues',
  })

  const hasFilters = syncFilters.length > 0

  const handleSyncClick = useCallback(() => {
    if (syncStatus === 'failed') {
      setModalStep('review_issues')
    } else {
      setModalStep(hasFilters ? 'filters' : 'issues')
    }
  }, [hasFilters, syncStatus])

  const handleCloseModal = useCallback(() => {
    setModalStep('closed')
  }, [])

  const actionLabel = getSyncActionLabel(syncStatus, integrationName, isExportMode)
  const isSyncDisabled = disabled || syncStatus === 'syncing' || isSyncing

  return (
    <>
      <F0Button
        variant="outline"
        label={actionLabel}
        onClick={handleSyncClick}
        disabled={isSyncDisabled}
        tooltip={isSyncDisabled && tooltip ? tooltip : undefined}
      />
      {modalStep === 'filters' && (
        <SyncFiltersModal
          syncFilters={syncFilters}
          skipConfirmationIfNoIssues={skipConfirmationIfNoIssues}
          onClose={handleCloseModal}
          onContinue={() => setModalStep('issues')}
        />
      )}
      {modalStep === 'issues' && (
        <Modals
          setShowModal={(show) => {
            if (!show) handleCloseModal()
          }}
          skipConfirmationIfNoIssues={skipConfirmationIfNoIssues}
        />
      )}
      {modalStep === 'review_issues' && (
        <SyncReviewModal
          isLoading={issuesLoading}
          onClose={handleCloseModal}
          issues={syncResultIssues}
          anyValidSyncable={false}
          title={i18n.t(
            'bookkeepers.clients.payroll_cycles.sync_status_stats.result_issues.modal_title'
          )}
          description={i18n.t(
            'bookkeepers.clients.payroll_cycles.sync_status_stats.result_issues.modal_description'
          )}
        />
      )}
    </>
  )
}
